import { createApi } from '@reduxjs/toolkit/query/react';
import { waServicesQuery } from './baseQueries';
import { WARootState } from './store';
import { toExportRole } from '@/utils/exportRole';
import { MimeType } from '@/utils/fileValidation';
import { triggerBlobDownload } from '@/utils/triggerBlobDownload';
import { buildRoleAwarePrefix } from '@/utils/api';
import { selectQueryViewingTag } from './selectQueryViewingTag';
import type { GridColDef } from '@mui/x-data-grid-premium';

export interface ExportColumnDefinition {
  Label?: string;
  Property: string;
  Type?: string;
}

export interface ExportTableRequest {
  /**
   * Suffix of the data endpoint being exported, e.g. `realizedGains`. The
   * role-aware `/api/<role>` (or `/api/advisor/Investors/<investorId>`)
   * prefix is added here, the same way the live grid queries build their
   * URL (see `buildRoleAwarePrefix`), so export always hits the same
   * investor/advisor scope as what's on screen.
   */
  dataUrl: string;
  /**
   * Either the grid's `GridColDef[]` (transformed into export column
   * definitions here) or already-built `ExportColumnDefinition[]` data.
   */
  columns: GridColDef[] | ExportColumnDefinition[];
  /** Investor being viewed, for advisor-scoped exports. Omit for the caller's own data. */
  investorId?: number;
  filename: string;
  params?: Record<string, unknown>;
  childProperty?: string;
}

const EXPORT_ROW_LIMIT = 5_000_000;

const isExportColumnDefinitions = (
  columns: GridColDef[] | ExportColumnDefinition[],
): columns is ExportColumnDefinition[] =>
  columns.every((column) => 'Property' in column);

const buildExportColumns = (
  columns: GridColDef[] | ExportColumnDefinition[],
): ExportColumnDefinition[] => {
  if (isExportColumnDefinitions(columns)) {
    return columns;
  }

  return columns
    .filter((column) => {
      return column.type !== 'actions';
    })
    .map((column) => {
      return {
        Label: column.headerName || '',
        Property: column.field,
        Type: column.type,
      };
    });
};

/**
 * Flattens a value into ASP.NET MVC model-binding query entries, e.g.
 * `{ sort: [{ Property: 'PercentChange', Direction: 'desc' }] }` becomes
 * `sort[0].Property=PercentChange&sort[0].Direction=desc`.
 */
function flattenParam(
  key: string,
  value: unknown,
  entries: [string, string][],
): void {
  if (value === undefined || value === null) {
    return;
  }
  if (value instanceof Date) {
    entries.push([key, value.toISOString()]);
    return;
  }
  if (Array.isArray(value)) {
    value.forEach((item, index) => {
      flattenParam(`${key}[${String(index)}]`, item, entries);
    });
    return;
  }
  if (typeof value === 'object') {
    Object.entries(value as Record<string, unknown>).forEach(
      ([childKey, childValue]) => {
        flattenParam(`${key}.${childKey}`, childValue, entries);
      },
    );
    return;
  }
  switch (typeof value) {
    case 'string':
      entries.push([key, value]);
      break;
    case 'number':
    case 'boolean':
    case 'bigint':
      entries.push([key, value.toString()]);
      break;
    default:
      entries.push([key, JSON.stringify(value)]);
  }
}

function normalizeParams(
  params: Record<string, unknown> = {},
): [string, string][] {
  const entries: [string, string][] = [];
  Object.entries(params).forEach(([key, value]) => {
    flattenParam(key, value, entries);
  });
  return entries;
}

type MergedExportParams =
  | { status: 'blocked' }
  | { status: 'ready'; params: Record<string, unknown> | undefined };

/**
 * Merges the global viewing-tag selection into export params the same way
 * `mergeViewingInvestorTagIntoPortfolioGainsArg` merges it into the live
 * gains queries (holdingsApi.ts): skipped when the caller already set
 * `InvestorTagId`/`TagId`, and never applied for an advisor-scoped export
 * (`investorId` set), since that's a different investor than the viewer.
 *
 * A persisted tag is not All accounts while `getUserTags` is still in flight.
 * `confirmedViewingTagSelection` collapses that pending result to null, which
 * would omit `InvestorTagId` and export every account. Block instead.
 */
function mergeViewingTagIntoExportParams(
  params: Record<string, unknown> | undefined,
  state: WARootState,
  investorId: number | undefined,
): MergedExportParams {
  if (investorId !== undefined) {
    return { status: 'ready', params };
  }
  if (
    params &&
    (Object.prototype.hasOwnProperty.call(params, 'InvestorTagId') ||
      Object.prototype.hasOwnProperty.call(params, 'TagId'))
  ) {
    return { status: 'ready', params };
  }
  const confirmed = selectQueryViewingTag(state);
  if (!confirmed.ready) {
    return { status: 'blocked' };
  }
  if (confirmed.selection === null) {
    return { status: 'ready', params };
  }
  return {
    status: 'ready',
    params: { ...params, InvestorTagId: confirmed.selection.tagId },
  };
}

const toQueryString = (params?: Record<string, unknown>): string => {
  const defaultParams: [string, string][] = [
    ['Limit', String(EXPORT_ROW_LIMIT)],
    ['Page', '1'],
    ['IsExport', 'true'],
    ...normalizeParams(params),
  ];

  const searchParams = new URLSearchParams(defaultParams);

  return searchParams.toString();
};

export const exportApi = createApi({
  reducerPath: 'exportApi',
  baseQuery: waServicesQuery,
  endpoints: (builder) => ({
    exportTable: builder.mutation<null, ExportTableRequest>({
      queryFn: async (
        { dataUrl, investorId, columns, filename, params, childProperty },
        { getState },
        _extra,
        baseQuery,
      ) => {
        const state = getState() as WARootState;
        const merged = mergeViewingTagIntoExportParams(
          params,
          state,
          investorId,
        );
        if (merged.status === 'blocked') {
          return {
            error: {
              status: 'CUSTOM_ERROR',
              error: 'Viewing tag is still loading',
            },
          };
        }
        // `/api/user/export` expects `DataUrl` with no leading slash and a
        // lowercase role segment (matching the `Currentrole` header below),
        // e.g. `api/investor/realizedGains` or `api/advisor/investors/123/...` —
        // unlike a live GET, where routing is case-insensitive either way.
        const prefix = buildRoleAwarePrefix(state.auth.currentRole, investorId)
          .replace(/^\/+/, '')
          .toLowerCase();
        const mergedParams = merged.params;

        const model = {
          Columns: buildExportColumns(columns),
          FileName: filename,
          Token: state.appView.UserInfo?.Token,
          ChildProperty: childProperty,
          DataUrl: `${prefix}/${dataUrl}?${toQueryString(mergedParams)}`,
          CurrentRole: toExportRole(state.auth.currentRole),
        };

        const result = await baseQuery({
          url: '/api/user/export',
          method: 'POST',
          body: model,
          responseHandler: async (response) => response.blob(),
        });

        if (result.error) {
          return { error: result.error };
        }

        await triggerBlobDownload(
          result.data as Blob,
          `${filename}.xls`,
          MimeType.Xls,
        );

        return { data: null };
      },
    }),
  }),
});

export const { useExportTableMutation } = exportApi;
